// ══════════════════════════════════════════════════════════════
// MOTEUR D'EXPLICATION — CotaSafe
// Génère les explications en langage humain pour l'IDEL
// ══════════════════════════════════════════════════════════════

import type { ActeAnalyse, BSIResult, RegleAppliquee } from './types';
import { getReglesApplicables } from './bsiEngine';

export interface ExplicationBSI {
  // Résumé global
  resume: string;
  // Par période
  periodes: ExplicationPeriode[];
  // Règles appliquées
  regles: RegleAppliquee[];
  // Alertes
  alertes: string[];
  // Total
  totalMessage: string;
}

export interface ExplicationPeriode {
  nom: string;
  emoji: string;
  actes: ExplicationActe[];
}

export interface ExplicationActe {
  label: string;
  code: string;
  montant: string;
  taux: string;
  explication: string;
  inclus: boolean;
}

/**
 * Génère l'explication complète d'un résultat BSI
 */
export function genererExplication(result: BSIResult): ExplicationBSI {
  const periodes: ExplicationPeriode[] = [];
  const alertes: string[] = [];

  // Organiser par période
  const periodesMap: Record<string, ActeAnalyse[]> = {
    matin: [],
    midi: [],
    soir: [],
  };

  for (const acte of result.actes) {
    periodesMap[acte.acte.periode].push(acte);
  }

  const periodeStyles: Record<string, { nom: string; emoji: string }> = {
    matin: { nom: 'Matin', emoji: '🌅' },
    midi: { nom: 'Midi', emoji: '☀️' },
    soir: { nom: 'Soir', emoji: '🌙' },
  };

  for (const [key, actes] of Object.entries(periodesMap)) {
    if (actes.length === 0) continue;

    const style = periodeStyles[key];
    const explications: ExplicationActe[] = [];

    for (const acte of actes) {
      explications.push({
        label: acte.acte.acte.label,
        code: acte.codeNGAP,
        montant: acte.montantFinal.toFixed(2).replace('.', ',') + ' €',
        taux: acte.inclusDansForfait ? 'Inclus' : acte.tauxApplique === 0 ? 'Gratuit' : acte.tauxApplique === 50 ? '50%' : '100%',
        explication: acte.raison,
        inclus: acte.inclusDansForfait,
      });
    }

    periodes.push({
      nom: style.nom,
      emoji: style.emoji,
      actes: explications,
    });
  }

  // Résumé global
  const nbActesFacturables = result.actes.filter((a) => !a.inclusDansForfait && a.tauxApplique > 0).length;
  const nbActesInclus = result.actes.filter((a) => a.inclusDansForfait).length;
  const nbActesGratuits = result.actes.filter((a) => !a.inclusDansForfait && a.tauxApplique === 0).length;

  let resume = '';
  if (result.forfait) {
    resume = `Le forfait ${result.forfait.code} (${result.forfait.montantJournalier.toFixed(2).replace('.', ',')} €/j) est appliqué. `;
  }

  if (nbActesFacturables > 0) {
    resume += `${nbActesFacturables} acte(s) technique(s) facturé(s) en complément. `;
  }

  if (nbActesInclus > 0) {
    resume += `${nbActesInclus} acte(s) inclus dans le forfait (non facturés séparément). `;
  }

  if (nbActesGratuits > 0) {
    resume += `${nbActesGratuits} acte(s) non facturé(s) — règle de cumul. `;
  }

  if (result.contradictions.length > 0) {
    alertes.push(...result.contradictions.map((c) => `⚠️ ${c.titre} : ${c.description}`));
  }

  // Total
  const total = result.totalJournalier.toFixed(2).replace('.', ',');
  let totalMessage = `Total journalier : ${total} €`;
  if (result.forfait) {
    const actesTotal = result.actes.reduce((s, a) => s + a.montantFinal, 0).toFixed(2).replace('.', ',');
    const ifiTotal = result.ifi.totalIFI.toFixed(2).replace('.', ',');
    let decomposition = `Forfait ${result.forfait.code} : ${result.forfait.montantJournalier.toFixed(2).replace('.', ',')} €`;
    decomposition += ` + Actes : ${actesTotal} €`;
    if (result.ifi.nbVisites > 0) {
      decomposition += ` + IFI ×${result.ifi.nbVisites} : ${ifiTotal} €`;
    }
    const applicableMaj = result.majorations.filter((m) => m.applicable);
    if (applicableMaj.length > 0) {
      const majTotal = applicableMaj.reduce((s, m) => s + m.montant, 0).toFixed(2).replace('.', ',');
      decomposition += ` + Majorations : ${majTotal} €`;
    }
    decomposition += ` = Total : ${total} €`;
    totalMessage = decomposition;
  }

  return {
    resume: resume.trim(),
    periodes,
    regles: getReglesApplicables(result),
    alertes,
    totalMessage,
  };
}

/**
 * Génère une explication "POURQUOI ?" plus narrative
 */
export function genererPourquoi(result: BSIResult): string[] {
  const lignes: string[] = [];

  // 1. Contexte BSI
  if (result.forfait) {
    lignes.push(`Le patient est pris en charge dans le cadre d'un BSI avec forfait ${result.forfait.code} — ${result.forfait.label}.`);
    lignes.push(`Ce forfait couvre la toilette, le change, le nursing, l'hydratation, l'alimentation, la prévention des escarres, la mobilisation et l'administration de traitement.`);
  } else {
    lignes.push('Aucun forfait BSI n\'a été détecté. Les actes sont facturés selon les règles NGAP classiques.');
  }

  // 2. Actes inclus dans le forfait
  const inclus = result.actes.filter((a) => a.inclusDansForfait);
  if (inclus.length > 0) {
    const labels = inclus.map((a) => a.acte.acte.label).join(', ');
    lignes.push(`La sélection contient « ${labels} » — ces actes sont déjà inclus dans le forfait ${result.forfait?.code} et ne sont pas facturés séparément.`);
  }

  // 3. Actes dérogatoires
  const derogatoires = result.actes.filter((a) => a.exceptionAppliquee?.includes('DÉROGATOIRE'));
  if (derogatoires.length > 0) {
    const labels = derogatoires.map((a) => `${a.codeNGAP} — ${a.acte.acte.label}`).join(' ; ');
    lignes.push(`${labels} : cet acte est dérogatoire selon l'Article A12 du Titre XVI. Il reste facturé en AMI à taux plein individuellement, mais il est soumis à l'Article 11B lorsqu'il y a plusieurs actes dans la même période.`);
  }

  // 4. Surveillance diabétique — Absorption glycémie
  const survDiab = result.actes.filter((a) => a.exceptionAppliquee?.includes('SURVEILLANCE DIABÉTIQUE'));
  if (survDiab.length > 0) {
    lignes.push('COTATION RETENUE');
    lignes.push('Analyse automatique : le contrôle glycémique a été intégré à l\'acte de surveillance diabétique (Art. 5bis) et n\'a pas fait l\'objet d\'une cotation séparée.');
  }

  // 5. Injection insuline → AMX sous BSI
  const insulineAMX = result.actes.filter((a) => a.raison?.includes('insuline') && a.codeNGAP?.includes('AMX'));
  if (insulineAMX.length > 0) {
    lignes.push('Analyse automatique : les injections d\'insuline ont été analysées selon leur nature et les règles de cumul applicables. L\'injection sous-cutanée d\'insuline est automatiquement convertie en AMX (jour de facturation BSI).');
  }

  // 6. Conversion AMX
  const actesAMX = result.actes.filter((a) => a.codeNGAP.includes('AMX'));
  if (actesAMX.length > 0) {
    const labels = actesAMX.map((a) => a.acte.acte.label).join(', ');
    lignes.push(`« ${labels} » : sous BSI, les actes techniques non dérogatoires sont convertis en AMX à 50% du coefficient.`);
  }

  // 7. Règle de cumul — Niveau 2 (entre actes techniques)
  const actesDecotes = result.actes.filter((a) => a.tauxApplique === 50 && !a.inclusDansForfait);
  if (actesDecotes.length > 0) {
    const labels = actesDecotes.map((a) => a.acte.acte.label).join(', ');
    lignes.push(`« ${labels} » : 2e acte de la période → facturé à 50% selon l'Article 11B (règle 100/50/0). Cette décote s'applique aussi aux actes dérogatoires lorsqu'ils sont en 2e position dans une même période.`);
  }

  const actesGratuits = result.actes.filter((a) => a.tauxApplique === 0 && !a.inclusDansForfait);
  if (actesGratuits.length > 0) {
    const labels = actesGratuits.map((a) => a.acte.acte.label).join(', ');
    lignes.push(`« ${labels} » : 3e acte et suivants → non facturés (gratuits) selon l'Article 11B.`);
  }

  // 8. Immunité Article 7
  const actes5bisCumul = result.actes.filter((a) => a.regleCumul?.includes('5bis'));
  const actesImmunes7 = result.actes.filter((a) => a.regleCumul?.includes('Art. 7'));

  // 9. IFI — indemnité de déplacement sous BSI
  if (result.ifi.nbVisites > 0) {
    lignes.push(`Déplacement : ${result.ifi.nbVisites} visite(s) × IFI ${result.ifi.montantUnitaire.toFixed(2).replace('.', ',')} € = ${result.ifi.totalIFI.toFixed(2).replace('.', ',')} €. Sous BSI, le déplacement est codé en IFI au même tarif que l'IFD (2,75 €).`);
  }

  if (actes5bisCumul.length > 0) {
    const labels = actes5bisCumul.map((a) => a.acte.acte.label).join(', ');
    lignes.push(`« ${labels} » : acte(s) Article 5bis — traités dans le cumul général Art. 11B (100/50/0).`);
  }

  if (actesImmunes7.length > 0) {
    const labels = actesImmunes7.map((a) => a.acte.acte.label).join(', ');
    lignes.push(`« ${labels} » : bénéficie(nt) de l'immunité Article 7 — pas de décote, quel que soit le nombre d'actes dans la période.`);
  }

  return lignes;
}